<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { todayKey } from '@/utils/date'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { AppButton } from '@/design-system/components'
import { lessonsApi, classesApi } from '@/api'
import { getColour } from '@/constants/colors'

const props = defineProps<{
  modelValue: boolean
  classId?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  created: []
}>()

interface LessonForm {
  classId: string
  date: string
  startTime: string
  endTime: string
  room: string
  title: string
}

const form = ref<LessonForm>({
  classId: '',
  date: todayKey(),
  startTime: '09:00',
  endTime: '10:00',
  room: '',
  title: '',
})

const saving = ref(false)
const error = ref<string | null>(null)
const classes = ref<Array<{ id: string; name: string; colour?: string }>>([])
const loadingClasses = ref(false)

const effectiveClassId = computed(() => props.classId || form.value.classId)
const selectedClass = computed(() => classes.value.find(c => c.id === effectiveClassId.value))

const canSave = computed(() => effectiveClassId.value && form.value.date && form.value.startTime && form.value.endTime)

async function loadClasses() {
  if (props.classId || classes.value.length > 0) return
  loadingClasses.value = true
  try {
    const res = await classesApi.classesGet()
    classes.value = (res.data as any[]).map((c: any) => ({ id: c.id, name: c.name, colour: c.colour }))
  } catch { /* ignore */ } finally {
    loadingClasses.value = false
  }
}

function close() {
  emit('update:modelValue', false)
}

function resetForm() {
  form.value = {
    classId: '',
    date: todayKey(),
    startTime: '09:00',
    endTime: '10:00',
    room: '',
    title: '',
  }
  error.value = null
}

async function handleSubmit() {
  if (!canSave.value || saving.value) return

  saving.value = true
  error.value = null

  try {
    const startDateTime = `${form.value.date}T${form.value.startTime}:00`
    const endDateTime = `${form.value.date}T${form.value.endTime}:00`

    await lessonsApi.createLesson({
      classId: effectiveClassId.value,
      startTime: startDateTime,
      endTime: endDateTime,
      room: form.value.room?.trim() || undefined,
      title: form.value.title?.trim() || undefined,
    })

    emit('created')
    close()
  } catch (err: unknown) {
    console.error('Failed to create lesson:', err)
    error.value = 'Failed to add lesson. Please try again.'
  } finally {
    saving.value = false
  }
}

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      resetForm()
      loadClasses()
    }
  }
)
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="modelValue" class="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] p-4">
        <div class="absolute inset-0 bg-black/30 backdrop-blur-[2px]" @click="close" />

        <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
          <!-- Header -->
          <div class="px-6 pt-6 pb-4">
            <div class="flex items-center justify-between mb-1">
              <div class="flex items-center gap-3">
                <div class="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
                  <font-awesome-icon :icon="['fas', 'chalkboard-user']" class="text-blue-500 text-sm" />
                </div>
                <h2 class="text-lg font-bold text-gray-900">Add Lesson</h2>
              </div>
              <button class="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" @click="close">
                <font-awesome-icon :icon="['fas', 'xmark']" />
              </button>
            </div>
          </div>

          <!-- Form -->
          <form class="px-6 pb-2 space-y-5" @submit.prevent="handleSubmit">
            <!-- Class selector (only when classId not provided) -->
            <div v-if="!classId">
              <label class="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Class</label>
              <div v-if="loadingClasses" class="flex items-center gap-2 text-sm text-gray-400 py-2">
                <font-awesome-icon :icon="['fas', 'spinner']" class="animate-spin text-xs" />
                Loading...
              </div>
              <div v-else class="grid grid-cols-2 gap-1.5">
                <button
                  v-for="cls in classes"
                  :key="cls.id"
                  type="button"
                  class="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-all border"
                  :class="form.classId === cls.id
                    ? 'border-bc-indigo-300 bg-bc-indigo-50/60 shadow-sm'
                    : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50/50'"
                  @click="form.classId = cls.id"
                >
                  <div class="w-3 h-3 rounded-full flex-shrink-0" :style="{ backgroundColor: getColour(cls.colour).hex }" />
                  <span class="text-sm font-medium text-gray-800 truncate">{{ cls.name }}</span>
                </button>
              </div>
            </div>

            <!-- Selected class pill (when classId provided) -->
            <div v-else-if="selectedClass" class="flex items-center gap-2">
              <div class="w-3 h-3 rounded-full" :style="{ backgroundColor: getColour(selectedClass.colour).hex }" />
              <span class="text-sm font-medium text-gray-600">{{ selectedClass.name }}</span>
            </div>

            <!-- Date & Time -->
            <div>
              <label class="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">When</label>
              <div class="flex gap-2">
                <input
                  v-model="form.date"
                  type="date"
                  required
                  class="flex-1 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-bc-indigo-500/20 focus:border-bc-indigo-400 transition-colors"
                />
                <input
                  v-model="form.startTime"
                  type="time"
                  required
                  class="w-[100px] px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 text-center focus:outline-none focus:ring-2 focus:ring-bc-indigo-500/20 focus:border-bc-indigo-400 transition-colors"
                />
                <span class="self-center text-gray-300 text-sm">-</span>
                <input
                  v-model="form.endTime"
                  type="time"
                  required
                  class="w-[100px] px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 text-center focus:outline-none focus:ring-2 focus:ring-bc-indigo-500/20 focus:border-bc-indigo-400 transition-colors"
                />
              </div>
            </div>

            <!-- Room & Title -->
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Room</label>
                <input
                  v-model="form.room"
                  type="text"
                  placeholder="Optional"
                  class="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-bc-indigo-500/20 focus:border-bc-indigo-400 transition-colors"
                />
              </div>
              <div>
                <label class="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Title</label>
                <input
                  v-model="form.title"
                  type="text"
                  placeholder="Optional"
                  class="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-bc-indigo-500/20 focus:border-bc-indigo-400 transition-colors"
                />
              </div>
            </div>

            <!-- Error -->
            <div v-if="error" class="px-3.5 py-2.5 bg-red-50 rounded-xl text-red-600 text-sm">
              {{ error }}
            </div>
          </form>

          <!-- Footer -->
          <div class="flex items-center justify-end gap-2.5 px-6 py-4 mt-1">
            <AppButton
              variant="secondary"
              size="md"
              @click="close"
            >
              Cancel
            </AppButton>
            <AppButton
              variant="primary"
              size="md"
              :disabled="!canSave"
              :loading="saving"
              @click="handleSubmit"
            >
              Add Lesson
            </AppButton>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
